import { FC, PropsWithChildren, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Action } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import styles from './styles.less';

const MAGRITTE_STATIC_SIZE_24 = 24;

interface DrawerProps {
    title: ReactNode;
    options?: ReactNode;
    footer: (ref: RefObject<HTMLElement>) => ReactNode;
    visible: boolean;
    onClose: () => void;
}

const Drawer: FC<PropsWithChildren<DrawerProps>> = ({ children, title, options, footer, visible, onClose }) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<number | string>('auto');
    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const onKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.body.classList.add(styles.noScroll);
        document.addEventListener('keydown', onKeydown, { once: true });

        return () => {
            document.body.classList.remove(styles.noScroll);
            window.removeEventListener('keydown', onKeydown);
        };
    }, [visible, onClose]);

    useLayoutEffect(() => {
        if (!visible) {
            return undefined;
        }

        const calculateMaxHeight = () => {
            if (!wrapperRef.current || !headerRef.current || !footerRef.current) {
                return 'auto';
            }
            return (
                wrapperRef.current.offsetHeight -
                headerRef.current.offsetHeight -
                (optionsRef?.current?.offsetHeight || 0) -
                footerRef.current.offsetHeight -
                MAGRITTE_STATIC_SIZE_24 * 2
            );
        };

        const recalculateMaxHeight = () => setMaxHeight(calculateMaxHeight());

        window.addEventListener('resize', recalculateMaxHeight);

        recalculateMaxHeight();

        return () => window.removeEventListener('resize', recalculateMaxHeight);
    }, [visible]);

    if (!visible) {
        return null;
    }

    return createPortal(
        <>
            <div className={styles.drawerWrapper} ref={wrapperRef}>
                <div ref={headerRef} className={styles.header}>
                    {title}
                    <Action onClick={onClose} icon={CrossOutlinedSize24} mode="secondary" style="neutral" />
                </div>
                {options && (
                    <div ref={optionsRef} className={styles.options}>
                        {options}
                    </div>
                )}
                <div className={styles.content} style={{ height: maxHeight }}>
                    {children}
                </div>
                {footer(footerRef)}
            </div>
            <div className={styles.overlay} onClick={onClose} />
        </>,
        document.body
    );
};

export default Drawer;
